/**
 * TrayQRAdminPage — Print-ready QR code sheet per hotel.
 * Route: /admin/tray-qr-codes
 *
 * Generates a grid of QR stickers for every registered tray. Operators print,
 * cut, and stick onto physical trays. Supports both guest and staff QR modes.
 */
import { useEffect, useState, useCallback } from 'react';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import { Printer, RefreshCw, Package, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BACKEND_URL } from '@/lib/backendUrl';

const PUBLIC_BASE = typeof window !== 'undefined' ? window.location.origin : 'https://getbitsy.ai';

export default function TrayQRAdminPage() {
  const [hotelId, setHotelId] = useState('hotel_grand_marina');
  const [hotels, setHotels] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHotels = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/hotels/public/list`);
      const d = await r.json();
      setHotels(d.hotels || d || []);
    } catch { /* noop */ }
  }, []);

  const loadTrays = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/tray/hotel/${hotelId}/all`);
      const d = await r.json();
      setData(d);
    } catch {
      setData({ hotel: { name: hotelId }, trays: [], count: 0 });
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => { loadHotels(); }, [loadHotels]);
  useEffect(() => { loadTrays(); }, [loadTrays]);

  const doPrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#f7f8fb] text-[#0a0e1a] py-6 print:bg-white print:py-0" data-testid="tray-qr-admin">
      {/* Toolbar (hidden in print) */}
      <div className="max-w-5xl mx-auto px-4 mb-6 print:hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold">Tray QR codes</h1>
            <p className="text-sm text-gray-600">Print-ready stickers. One per physical tray.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={hotelId}
              onChange={(e) => setHotelId(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="hotel-select"
            >
              <option value="hotel_grand_marina">The Grand Marina Hotel</option>
              {hotels.filter((h) => h.id !== 'hotel_grand_marina').map((h) => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
            </select>
            <Button variant="outline" onClick={loadTrays} disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button onClick={doPrint} className="bg-black text-white" data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>
      </div>

      {/* Print header */}
      <div className="max-w-5xl mx-auto px-4 mb-4 print:px-8">
        <div className="border-b border-gray-300 pb-3">
          <p className="text-xs uppercase tracking-wider text-gray-500">getbitsy · Tray QR sheet</p>
          <h2 className="text-lg font-semibold mt-1" data-testid="sheet-hotel-name">
            {data?.hotel?.name || 'Loading…'}
          </h2>
          <p className="text-xs text-gray-500">
            {data?.count || 0} tray{(data?.count || 0) !== 1 ? 's' : ''} · {PUBLIC_BASE}
          </p>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-5xl mx-auto px-4 print:px-8">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : (data?.trays || []).length === 0 ? (
          <div className="text-center py-12" data-testid="empty-trays">
            <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No trays registered for this hotel.</p>
            <p className="text-xs text-gray-400 mt-1">POST to /api/tray/register or add par-levels first.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-3 print:gap-3">
            {data.trays.map((t) => (
              <div
                key={t.tray_id}
                className="bg-white border border-gray-300 rounded-xl p-4 break-inside-avoid print:border-2 print:border-black"
                data-testid={`qr-card-${t.tray_id}`}
              >
                <div className="text-center mb-2">
                  <Badge className="bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20 text-[10px]">
                    GUEST
                  </Badge>
                </div>
                <div className="flex justify-center bg-white py-2">
                  <QRCode
                    value={`${PUBLIC_BASE}/t/${t.tray_id}`}
                    size={160}
                    level="M"
                    includeMargin={false}
                    data-testid={`qr-guest-${t.tray_id}`}
                  />
                </div>
                <div className="mt-2 text-center">
                  <p className="text-sm font-semibold">Room {t.room_number}</p>
                  <p className="text-[10px] text-gray-500 font-mono">{t.tray_id}</p>
                  {t.label && <p className="text-[10px] text-gray-500">{t.label}</p>}
                </div>
                <div className="mt-3 pt-3 border-t border-dashed border-gray-300">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <Badge className="bg-cyan-500/10 text-cyan-700 border border-cyan-500/30 text-[9px]">
                      STAFF
                    </Badge>
                    <p className="text-[9px] text-gray-400 font-mono truncate">/s/{t.tray_id}</p>
                  </div>
                  <div className="flex justify-center">
                    <QRCode
                      value={`${PUBLIC_BASE}/s/${t.tray_id}`}
                      size={80}
                      level="M"
                      includeMargin={false}
                      fgColor="#0891b2"
                      data-testid={`qr-staff-${t.tray_id}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
